import { cn } from '@today/ui-primitives'
import { type MouseEvent, type ReactElement, type RefObject, useEffect, useRef, useState } from 'react'

/**
 * One crumb of a {@link Breadcrumb} trail.
 *
 * @property label - Visible text, already localised by the consumer.
 * @property href - Link target. Omit for a level that has no page of its own.
 */
export interface BreadcrumbItem {
	label: string
	href?: string
}

/**
 * Built-in strings of {@link Breadcrumb}, overridable per locale (tech §59).
 *
 * @property navigation - Accessible name of the `<nav>` landmark. Default `Breadcrumb`.
 * @property showHiddenItems - Accessible name of the expand control; a function because word
 *   order and plurals differ by locale. Default `Show N hidden items`.
 */
export interface BreadcrumbLabels {
	navigation: string
	showHiddenItems: (count: number) => string
}

const DEFAULT_LABELS: BreadcrumbLabels = {
	navigation: 'Breadcrumb',
	showHiddenItems: (count) => `Show ${count} hidden items`,
}

/**
 * Props for the {@link Breadcrumb} molecule.
 *
 * @property items - Crumbs from the root to the current page; the last one is the current page.
 * @property onNavigate - Called with an ancestor's `href` on a plain left click (client-side routing).
 * @property maxItems - Longest trail shown uncollapsed. Default `4`; values below `3` act as `3`.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 * @property className - Extra Tailwind classes merged onto the landmark.
 */
export interface BreadcrumbProps {
	items: BreadcrumbItem[]
	onNavigate?: (href: string) => void
	maxItems?: number
	labels?: Partial<BreadcrumbLabels>
	className?: string
}

const DEFAULT_MAX_ITEMS = 4
// A collapsed trail always shows the root, the expand control and the current page.
const MIN_MAX_ITEMS = 3
// A collapsed trail shows only the root before the expand control, so the first hidden crumb is always the second.
const FIRST_HIDDEN_INDEX = 1

/** A crumb plus its position in the full `items` array (labels may repeat, so the index is the key). */
interface IndexedCrumb {
	item: BreadcrumbItem
	index: number
}

/** What the list renders, in order: crumbs with an optional expand control after the root. */
interface VisibleTrail {
	head: IndexedCrumb[]
	hiddenCount: number
	tail: IndexedCrumb[]
}

const crumbClasses = 'min-w-0 truncate'
const linkClasses = cn(
	crumbClasses,
	'rounded-ds-sm text-ds-text-muted underline-offset-2 hover:text-ds-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
)
const currentClasses = cn(crumbClasses, 'font-semibold text-ds-text')
const plainClasses = cn(crumbClasses, 'text-ds-text-muted')
const expandClasses =
	'shrink-0 rounded-ds-sm px-ds-1 text-ds-text-muted hover:text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

/** A plain left click is routed in-app; modified clicks keep the browser default (new tab, …). */
const isPlainClick = (event: MouseEvent) =>
	event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey

/** Splits the trail into the shown root, the collapsed middle and the shown end (REQ-003). */
function visibleTrail(items: BreadcrumbItem[], maxItems: number, isExpanded: boolean): VisibleTrail {
	const crumbs = items.map((item, index) => ({ item, index }))
	const limit = Math.max(MIN_MAX_ITEMS, maxItems)
	if (isExpanded || crumbs.length <= limit) return { head: crumbs, hiddenCount: 0, tail: [] }
	const tailLength = limit - 2
	return {
		head: crumbs.slice(0, 1),
		hiddenCount: crumbs.length - 1 - tailLength,
		tail: crumbs.slice(crumbs.length - tailLength),
	}
}

/** The decorative glyph between crumbs; hidden so screen readers read only the labels. */
function Separator(): ReactElement {
	return (
		<span aria-hidden="true" className="shrink-0 text-ds-text-muted">
			›
		</span>
	)
}

interface CrumbProps {
	item: BreadcrumbItem
	isCurrent: boolean
	onNavigate?: (href: string) => void
	focusRef?: RefObject<HTMLElement | null>
}

/** One crumb: the current page as text, an ancestor as a link when it has an `href`, else as text. */
function Crumb({ item, isCurrent, onNavigate, focusRef }: CrumbProps): ReactElement {
	const { label, href } = item
	if (isCurrent) {
		return (
			<span aria-current="page" title={label} className={currentClasses}>
				{label}
			</span>
		)
	}
	if (href === undefined) {
		// tabIndex -1 only when it must take focus after an expand (REQ-003); it never joins the Tab order.
		return (
			<span
				ref={focusRef as RefObject<HTMLSpanElement | null>}
				tabIndex={focusRef ? -1 : undefined}
				title={label}
				className={plainClasses}
			>
				{label}
			</span>
		)
	}
	return (
		<a
			ref={focusRef as RefObject<HTMLAnchorElement | null>}
			href={href}
			title={label}
			className={linkClasses}
			onClick={(event) => {
				if (!onNavigate || !isPlainClick(event)) return
				event.preventDefault()
				onNavigate(href)
			}}
		>
			{label}
		</a>
	)
}

/**
 * The trail from the root to the current page on a nested or detail screen.
 *
 * @remarks
 * - **Accessible:** a `<nav>` landmark around an ordered list; the last crumb is the current
 *   page (`aria-current="page"`, never a link) and separators are hidden from assistive tech.
 * - **Router-agnostic:** ancestors are real links (new tab, copy link); `onNavigate` receives a
 *   plain left click for client-side routing, as in `AdminShellView`.
 * - **Deep trails collapse, never drop:** past `maxItems` the middle crumbs sit behind one expand
 *   control; activating it shows the full trail and focuses the first revealed crumb.
 * - **One line:** labels shrink and end in an ellipsis when the trail is wider than its container;
 *   the full label stays the accessible name and the tooltip.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @example
 * ```tsx
 * <Breadcrumb items={[{ label: t('nav.home'), href: '/' }, { label: name }]} onNavigate={navigate} />
 * ```
 *
 * @see packages/ui-components/src/breadcrumb/breadcrumb.stories.tsx for visual examples
 */
export function Breadcrumb({
	items,
	onNavigate,
	maxItems = DEFAULT_MAX_ITEMS,
	labels,
	className,
}: BreadcrumbProps): ReactElement | null {
	const [isExpanded, setIsExpanded] = useState(false)
	const firstHiddenRef = useRef<HTMLElement | null>(null)

	// The expand control unmounts on activation; hand focus to the first revealed crumb so it
	// does not fall back to <body>. Only an expand can make isExpanded true, never the first render.
	useEffect(() => {
		if (isExpanded) firstHiddenRef.current?.focus()
	}, [isExpanded])

	if (items.length === 0) return null
	const text = { ...DEFAULT_LABELS, ...labels }
	const lastIndex = items.length - 1
	const { head, hiddenCount, tail } = visibleTrail(items, maxItems, isExpanded)

	const renderCrumb = ({ item, index }: IndexedCrumb, position: number) => (
		<li key={index} className="flex min-w-0 items-center gap-ds-2">
			{position > 0 ? <Separator /> : null}
			<Crumb
				item={item}
				isCurrent={index === lastIndex}
				onNavigate={onNavigate}
				focusRef={isExpanded && index === FIRST_HIDDEN_INDEX ? firstHiddenRef : undefined}
			/>
		</li>
	)

	return (
		<nav aria-label={text.navigation} className={cn('min-w-0', className)}>
			<ol className="flex min-w-0 flex-nowrap items-center gap-ds-2 text-ds-sm">
				{head.map((crumb, position) => renderCrumb(crumb, position))}
				{hiddenCount > 0 ? (
					<li key="expand" className="flex shrink-0 items-center gap-ds-2">
						<Separator />
						<button
							type="button"
							aria-label={text.showHiddenItems(hiddenCount)}
							className={expandClasses}
							onClick={() => setIsExpanded(true)}
						>
							<span aria-hidden="true">…</span>
						</button>
					</li>
				) : null}
				{tail.map((crumb, position) => renderCrumb(crumb, head.length + 1 + position))}
			</ol>
		</nav>
	)
}
